import React, { useEffect, useState } from "react";
import axios from 'axios';

export default function QuizManagement() {
    const [showForm, setShowForm] = useState(false);
    const [quizzes, setQuizzes] = useState([]);
    const [quizName, setQuizName] = useState('');
    const [fullMark, setFullMark] = useState('');
    const [quizDetail, setQuizDetail] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const quizzesPerPage = 15;

    useEffect(() => {
        const fetchQuizzes = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getQuizzes`);
                setQuizzes(response.data.data);
            } catch (error) {
                console.error('Error fetching quizzes:', error);
                alert('Failed to fetch quizzes.');
            }
        };
        fetchQuizzes();
    }, []);

    const handleCreateQuiz = () => {
        setShowForm(true);
        setQuizName('');
        setFullMark('');
        setQuizDetail('');
    };

    const handleQuizSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = {
            quiz_name: quizName,
            full_mark: parseInt(fullMark),
            quiz_detail: quizDetail || null,
        };
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createQuiz`, formData);
            if (response.status === 201) {
                setShowForm(false);
                setQuizName('');
                setFullMark('');
                setQuizDetail('');
                const updatedQuizzes = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getQuizzes`);
                setQuizzes(updatedQuizzes.data.data);
                setCurrentPage(1); // Reset to first page
            }
        } catch (error) {
            console.error('Error creating quiz:', error);
            alert(error.response?.data?.error || 'Failed to create quiz.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setQuizName('');
        setFullMark('');
        setQuizDetail('');
    };

    // Filter quizzes based on search query
    const filteredQuizzes = quizzes.filter(quiz =>
        quiz.quiz_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (quiz.quiz_detail && quiz.quiz_detail.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Pagination logic
    const indexOfLast = currentPage * quizzesPerPage;
    const indexOfFirst = indexOfLast - quizzesPerPage;
    const currentQuizzes = filteredQuizzes.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredQuizzes.length / quizzesPerPage);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="flex-1 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Quiz Management</h1>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <input
                            type="text"
                            placeholder="Search quizzes..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1); // Reset to first page on search
                            }}
                            className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button 
                            onClick={handleCreateQuiz}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
                        >
                            <span>Create Quiz</span>
                        </button>
                    </div>
                </div>

                {/* Quizzes Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-4 sm:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-4">Quizzes</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-blue-100 sticky top-0">
                                        <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Quiz Name</th>
                                        <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Full Mark</th>
                                        <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Quiz Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentQuizzes.length > 0 ? currentQuizzes.map((quiz, index) => (
                                        <tr key={quiz.quiz_name} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                                            <td className="px-4 py-2">{quiz.quiz_name}</td>
                                            <td className="px-4 py-2">{quiz.full_mark}</td>
                                            <td className="px-4 py-2">{quiz.quiz_detail || '-'}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-2 text-center text-gray-500">
                                                No quizzes found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-between items-center mt-6">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <select
                                    value={currentPage}
                                    onChange={(e) => setCurrentPage(Number(e.target.value))}
                                    className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                                        <option key={page} value={page}>
                                            Page {page} of {totalPages}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Quiz Modal */}
            {showForm && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Create New Quiz</h2>
                        <form onSubmit={handleQuizSubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Quiz Name</label>
                                    <input
                                        type="text"
                                        value={quizName}
                                        onChange={(e) => setQuizName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Full Mark</label>
                                    <input
                                        type="number"
                                        value={fullMark}
                                        onChange={(e) => setFullMark(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                        min="0"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Quiz Detail (Optional)</label>
                                    <textarea
                                        value={quizDetail}
                                        onChange={(e) => setQuizDetail(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows="4"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${loading && "opacity-50 cursor-not-allowed"}`}
                                    disabled={loading}
                                >
                                    {loading ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}